import React, { useEffect, useState, useCallback } from 'react';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { images, companyInfo } from '../data/mockData';

const HeroSection = ({ content }) => {
  const [scrollY, setScrollY] = useState(0);

  const handleScroll = useCallback(() => {
    // Use requestAnimationFrame for smooth performance
    window.requestAnimationFrame(() => {
      setScrollY(window.scrollY);
    });
  }, []);

  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (!prefersReducedMotion) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToProducts = () => {
    const element = document.getElementById('products');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const trustItems = [
    'Quality Control',
    'Cold Chain',
    'Custom Packaging',
    'EU-Focused Delivery'
  ];

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Premium Hero Background Image */}
      <div className="absolute inset-0">
        <picture>
          <source
            media="(max-width: 768px)"
            srcSet={images.hero.main}
            width="1200"
            height="800"
          />
          <img
            src={images.hero.main}
            alt="Fresh Produce"
            loading="eager"
            width="1920"
            height="1080"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              transform: window.innerWidth > 768 ? `scale(${1 + scrollY * 0.0002})` : 'scale(1)',
              transition: 'transform 0.1s ease-out'
            }}
          />
        </picture>
        {/* Stronger dark navy gradient overlay for better readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/97 via-slate-900/95 to-slate-800/92" />
        
        {/* Subtle animated grid - disabled on mobile for performance */}
        <div
          className="hidden md:block absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '100px 100px',
            transform: `translateY(${scrollY * 0.3}px)`
          }}
        />
      </div>

      {/* Floating accent elements - lighter on mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-20 right-10 w-96 h-96 rounded-full opacity-5 md:opacity-10 blur-3xl bg-emerald-500"
          style={{
            transform: window.innerWidth > 768 ? `translateY(${scrollY * 0.15}px)` : 'none'
          }}
        />
        <div
          className="absolute bottom-32 left-10 w-80 h-80 rounded-full opacity-5 md:opacity-10 blur-3xl bg-blue-500"
          style={{
            transform: window.innerWidth > 768 ? `translateY(${scrollY * -0.1}px)` : 'none'
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 py-32 text-center">
        <div className="max-w-5xl mx-auto space-y-10">
          {/* Headline */}
          <h1
            className="text-4xl md:text-7xl lg:text-8xl font-bold text-white leading-tight tracking-tight"
            style={{
              opacity: window.innerWidth > 768 ? Math.max(0, 1 - scrollY / 500) : 1,
              transform: window.innerWidth > 768 ? `translateY(${scrollY * 0.5}px)` : 'none'
            }}
          >
            {content.hero.headline}
          </h1>

          {/* Subheadline */}
          <p
            className="text-lg md:text-2xl text-slate-200 max-w-3xl mx-auto leading-relaxed font-light"
            style={{
              opacity: window.innerWidth > 768 ? Math.max(0, 1 - scrollY / 600) : 1,
              transform: window.innerWidth > 768 ? `translateY(${scrollY * 0.4}px)` : 'none'
            }}
          >
            {content.hero.subheadline}
          </p>

          {/* CTAs */}
          <div
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
            style={{
              opacity: window.innerWidth > 768 ? Math.max(0, 1 - scrollY / 700) : 1,
              transform: window.innerWidth > 768 ? `translateY(${scrollY * 0.3}px)` : 'none'
            }}
          >
            <button
              onClick={scrollToContact}
              className="group px-10 py-5 bg-emerald-600 text-white rounded-xl font-semibold text-lg hover:bg-emerald-700 transition-all duration-300 flex items-center space-x-2 shadow-lg hover:shadow-emerald-600/50 hover:scale-105"
            >
              <span>{content.hero.cta1}</span>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={scrollToProducts}
              className="px-10 py-5 bg-white/10 backdrop-blur-sm md:backdrop-blur-md text-white rounded-xl font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/20 hover:scale-105"
            >
              {content.hero.cta2}
            </button>
          </div>

          {/* Trust Strip */}
          <div
            className="pt-8"
            style={{
              opacity: window.innerWidth > 768 ? Math.max(0, 1 - scrollY / 800) : 1,
              transform: window.innerWidth > 768 ? `translateY(${scrollY * 0.2}px)` : 'none'
            }}
          >
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
              {trustItems.map((item, index) => (
                <div key={index} className="flex items-center space-x-2 text-slate-300">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                  <span className="text-sm md:text-base font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* WhatsApp Quick Action */}
          <div className="pt-6">
            <a
              href={`https://wa.me/${companyInfo.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Quick inquiry via WhatsApp</span>
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator - hidden on mobile */}
      <div
        className="hidden md:block absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce"
        style={{ opacity: Math.max(0, 1 - scrollY / 300) }}
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
